/**
 * Троеточие в строке таблицы сотрудников: выпадающее меню назначения роли в миниаппе
 * (без боковой панели).
 */

import { useCallback, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import type { AssignableMiniappRole, Employee } from '../../api/employees';
import {
  ASSIGNABLE_MINIAPP_ROLES,
  ASSIGN_ROLE_MENU_LABELS,
  assignRole,
} from '../../api/employees';
import { toUserMessage } from '../../api/client';
import { Dropdown, MenuButton } from '../../uikit';
import { IconEdit, IconMoreVert } from '../../uikit/components/icons';
import { formatFullName } from '../../utils/formatFullName';

function effectiveAssignableRole(miniappRole: string | undefined): AssignableMiniappRole {
  return miniappRole === 'manager' ? 'manager' : 'employee';
}

const MenuItemButton = styled.button(
  ({ theme: { space, colors, fontSizes } }) => css`
    display: block;
    width: 100%;
    min-height: ${space[4]}px;
    padding: ${space[1]}px ${space[2]}px;
    border: none;
    background: ${colors.white};
    color: ${colors.text.primary};
    font-size: ${fontSizes[4]}px;
    font-family: inherit;
    font-weight: 500;
    text-align: left;
    cursor: pointer;
    transition: background 150ms ease;

    &:hover:not(:disabled) {
      background: ${colors.highlight.gray25};
    }

    &:disabled {
      cursor: not-allowed;
      color: ${colors.text.disabled};
      background: ${colors.white};
    }
  `,
);

const ActionsInline = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
`;

const EditIconLink = styled.a(
  ({ theme: { colors } }) => css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    min-width: 34px;
    height: 34px;
    min-height: 34px;
    color: ${colors.text.secondary};
    text-decoration: none;
    transition: 0.2s background-color ease-in-out;

    &:hover {
      color: ${colors.text.primary};
      background: ${colors.highlight.gray25};
    }
  `,
);

export interface EmployeeRowRoleDropdownProps {
  employee: Employee;
  editUrl: string;
  onAssigned: () => void;
  onAssignError: (message: string) => void;
}

export function EmployeeRowRoleDropdown({
  employee,
  editUrl,
  onAssigned,
  onAssignError,
}: EmployeeRowRoleDropdownProps) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const fullName =
    formatFullName(employee.familyName, employee.givenName, employee.patronymic) ||
    employee.email ||
    'сотрудника';

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
  }, []);

  const currentRole = effectiveAssignableRole(employee.miniappRole);

  const pickRole = useCallback(
    async (role: AssignableMiniappRole) => {
      if (role === currentRole) {
        setOpen(false);
        return;
      }
      setAssigning(true);
      try {
        await assignRole(employee.id, role);
        setOpen(false);
        onAssigned();
      } catch (err) {
        onAssignError(toUserMessage(err));
      } finally {
        setAssigning(false);
      }
    },
    [currentRole, employee.id, onAssigned, onAssignError],
  );

  return (
    <ActionsInline>
      <EditIconLink
        href={editUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Редактировать в основной системе: ${fullName}`}
      >
        <IconEdit />
      </EditIconLink>
      <Dropdown
        open={open}
        onOpenChange={handleOpenChange}
        placement="bottom-end"
        width="min(280px, calc(100vw - 24px))"
        menuZIndex={theme.zIndex.tooltip}
        trigger={
          <MenuButton
            size={34}
            aria-label={`Действия: ${fullName}`}
            aria-expanded={open}
            aria-haspopup="menu"
            active={open}
            disabled={assigning}
          >
            <IconMoreVert />
          </MenuButton>
        }
      >
        {ASSIGNABLE_MINIAPP_ROLES.map((role) => {
          const isCurrent = role === currentRole;
          return (
            <MenuItemButton
              key={role}
              type="button"
              role="menuitem"
              disabled={assigning || isCurrent}
              aria-current={isCurrent ? 'true' : undefined}
              onClick={() => void pickRole(role)}
            >
              {ASSIGN_ROLE_MENU_LABELS[role]}
            </MenuItemButton>
          );
        })}
      </Dropdown>
    </ActionsInline>
  );
}
